import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled, { keyframes } from 'styled-components';
const fadeUp = keyframes `
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const PageWrapper = styled.div `
  min-height: 100vh;
  background: var(--bg-primary);
`;
const HeroSection = styled.section `
  padding: 100px 24px 60px;
  text-align: center;
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);

  @media (max-width: 768px) {
    padding: 70px 16px 40px;
  }
`;
const HeroContent = styled.div `
  max-width: 800px;
  margin: 0 auto;
  animation: ${fadeUp} 0.8s ease-out;
`;
const PageTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 400;
  color: var(--text-primary);
  margin-bottom: 16px;
  letter-spacing: 0.02em;
`;
const LastUpdated = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-tertiary);
  font-style: italic;
`;
const ContentWrapper = styled.main `
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 24px 80px;
  animation: ${fadeUp} 0.8s ease-out 0.2s backwards;

  @media (max-width: 768px) {
    padding: 32px 16px 60px;
  }
`;
const Section = styled.section `
  margin-bottom: 40px;
`;
const SectionTitle = styled.h2 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 22px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-color);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 60px;
    height: 2px;
    background: var(--accent-gold, #B8860B);
  }
`;
const SubsectionTitle = styled.h3 `
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-top: 24px;
  margin-bottom: 12px;
`;
const Paragraph = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 16px;
`;
const List = styled.ul `
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
`;
const ListItem = styled.li `
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  padding: 8px 0 8px 28px;
  position: relative;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 16px;
    width: 8px;
    height: 8px;
    background: var(--accent-gold, #B8860B);
    opacity: 0.6;
  }
`;
const Link = styled.a `
  color: var(--accent-gold, #B8860B);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    text-decoration: underline;
  }
`;
const ContactCard = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 24px;
  margin-top: 16px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: var(--accent-gold, #B8860B);
  }
`;
const ContactText = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin: 0;

  strong {
    color: var(--text-primary);
    display: block;
    margin-bottom: 8px;
  }
`;
const NoticeBox = styled.div `
  background: var(--accent-gold-bg, rgba(184, 134, 11, 0.08));
  border: 1px solid var(--accent-gold, #B8860B);
  padding: 20px 24px;
  margin-top: 24px;

  strong {
    color: var(--accent-gold, #B8860B);
    display: block;
    margin-bottom: 8px;
  }

  p {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: var(--text-secondary);
    line-height: 1.7;
    margin: 0;
  }
`;
const PrivacyPolicy = () => {
    return (_jsxs(PageWrapper, { children: [_jsx(HeroSection, { children: _jsxs(HeroContent, { children: [_jsx(PageTitle, { children: "Privacy Policy" }), _jsx(LastUpdated, { children: "Last Updated: October 28, 2025" })] }) }), _jsxs(ContentWrapper, { children: [_jsxs(Section, { children: [_jsx(SectionTitle, { children: "1. Introduction" }), _jsx(Paragraph, { children: "Royal Gems, Inc. (\"we,\" \"our,\" or \"us\") operates a business-to-business diamond trading platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our authenticated dealer platform." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "2. Information We Collect" }), _jsx(SubsectionTitle, { children: "2.1 Account Information" }), _jsx(Paragraph, { children: "When you register as a diamond dealer, we collect:" }), _jsxs(List, { children: [_jsx(ListItem, { children: "Business name and contact information" }), _jsx(ListItem, { children: "Email address and authentication credentials" }), _jsx(ListItem, { children: "Business license and dealer verification documents" }), _jsx(ListItem, { children: "Professional credentials and references" })] }), _jsx(SubsectionTitle, { children: "2.2 Business Transaction Data" }), _jsxs(List, { children: [_jsx(ListItem, { children: "Memo requests and diamond preferences" }), _jsx(ListItem, { children: "Inventory browsing history" }), _jsx(ListItem, { children: "Search filters and saved preferences" }), _jsx(ListItem, { children: "Communication with our team" })] }), _jsx(SubsectionTitle, { children: "2.3 Technical Information" }), _jsxs(List, { children: [_jsx(ListItem, { children: "IP address and device information" }), _jsx(ListItem, { children: "Browser type and version" }), _jsx(ListItem, { children: "Session data and authentication tokens" }), _jsx(ListItem, { children: "Usage analytics and performance metrics" })] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "3. How We Use Your Information" }), _jsx(Paragraph, { children: "We use collected information for:" }), _jsxs(List, { children: [_jsx(ListItem, { children: "Providing and maintaining our B2B platform services" }), _jsx(ListItem, { children: "Verifying dealer credentials and business legitimacy" }), _jsx(ListItem, { children: "Processing memo requests and managing diamond inventory" }), _jsx(ListItem, { children: "Improving platform features and user experience" }), _jsx(ListItem, { children: "Communicating about your account and transactions" }), _jsx(ListItem, { children: "Ensuring platform security and preventing fraud" }), _jsx(ListItem, { children: "Complying with legal obligations" })] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "4. Information Sharing" }), _jsx(Paragraph, { children: "We do not sell your personal information. We may share information only in these circumstances:" }), _jsxs(List, { children: [_jsxs(ListItem, { children: [_jsx("strong", { children: "Service Providers:" }), " With trusted third parties who assist in platform operations (hosting, analytics)"] }), _jsxs(ListItem, { children: [_jsx("strong", { children: "Business Partners:" }), " With verified suppliers when processing memo requests"] }), _jsxs(ListItem, { children: [_jsx("strong", { children: "Legal Requirements:" }), " When required by law or to protect our rights"] }), _jsxs(ListItem, { children: [_jsx("strong", { children: "Business Transfers:" }), " In connection with a merger, acquisition, or sale of assets"] })] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "5. Data Security" }), _jsx(Paragraph, { children: "We implement industry-standard security measures to protect your information:" }), _jsxs(List, { children: [_jsx(ListItem, { children: "SSL/TLS encryption for all data transmission" }), _jsx(ListItem, { children: "Secure password hashing with bcrypt" }), _jsx(ListItem, { children: "JWT-based authentication with secure token management" }), _jsx(ListItem, { children: "Role-based access control (RBAC) for sensitive data" }), _jsx(ListItem, { children: "Regular security audits and vulnerability assessments" }), _jsx(ListItem, { children: "Encrypted database storage on Microsoft Azure" })] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "6. Your Rights" }), _jsx(Paragraph, { children: "You have the right to:" }), _jsxs(List, { children: [_jsx(ListItem, { children: "Access your personal information" }), _jsx(ListItem, { children: "Correct inaccurate data" }), _jsx(ListItem, { children: "Request deletion of your account" }), _jsx(ListItem, { children: "Export your data" }), _jsx(ListItem, { children: "Opt-out of marketing communications" }), _jsx(ListItem, { children: "Withdraw consent where applicable" })] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "7. Data Retention" }), _jsx(Paragraph, { children: "We retain your information for as long as your account is active or as needed to provide services. After account closure, we may retain certain information for legal compliance, dispute resolution, and fraud prevention purposes." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "8. Cookies and Tracking" }), _jsx(Paragraph, { children: "We use essential cookies for authentication and session management. We may use analytics cookies to improve our platform. You can control cookie preferences through your browser settings." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "9. Third-Party Links" }), _jsx(Paragraph, { children: "Our platform may contain links to third-party diamond certification authorities (GIA, AGS, IGI, etc.). We are not responsible for their privacy practices." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "10. Children's Privacy" }), _jsx(Paragraph, { children: "Our platform is intended for business use only. We do not knowingly collect information from individuals under 18 years of age." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "11. International Data Transfers" }), _jsx(Paragraph, { children: "Your information may be transferred to and processed in the United States where our servers are located. We ensure appropriate safeguards are in place for such transfers." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "12. Changes to This Policy" }), _jsx(Paragraph, { children: "We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the \"Last Updated\" date." })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "13. Contact Us" }), _jsx(Paragraph, { children: "If you have questions about this Privacy Policy or our data practices, please contact us:" }), _jsx(ContactCard, { children: _jsxs(ContactText, { children: [_jsx("strong", { children: "Royal Gems, Inc." }), "Email: ", _jsx(Link, { href: "mailto:privacy@royalgems.com", children: "privacy@royalgems.com" }), _jsx("br", {}), "Security: ", _jsx(Link, { href: "mailto:security@royalgems.com", children: "security@royalgems.com" })] }) })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "14. Legitimate Business Operations" }), _jsxs(NoticeBox, { children: [_jsx("strong", { children: "Important Notice" }), _jsx("p", { children: "Royal Gems, Inc. is a legitimate business-to-business diamond trading platform. Our authentication system and information collection practices are standard for secure B2B commerce platforms. We are committed to protecting your privacy and maintaining the highest standards of data security." })] })] })] })] }));
};
export default PrivacyPolicy;
